import React from 'react'
import styled from 'styled-components'

import * as themeUtilities from '@/utilities/client-components/utilities/themeUtils'

import StyledErrorMessage from './StyledErrorMessage'
import StyledFlexColumn from './StyledFlexColumn'
import StyledFlexRow from './StyledFlexRow'
import StyledLabel from './StyledLabel'
import StyledRequiredIndicator from './StyledRequiredIndicator'

const VanillaInput = styled.input`
  appearance: none;
  padding: 10px 0;
  width: 100%;
  color: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'input')};
  font-size: ${(props: any) => themeUtilities.getFontSize(props.theme, 'default')}px;
  border: none;
  background: transparent;
  outline: none;

  &::placeholder {
    color: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'input.placeholderColor')};
  }
`

const StyledInputComponent = styled(VanillaInput)`
  margin: 5px 0;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'input.borderColor')};
  background: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'input.backgroundColor')};
`

const StyledInputWrapper = styled.div`
  margin: 5px 0;
  padding: 0 10px;
  border-radius: 5px;
  border: 1px solid ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'input.borderColor')};
  color: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'input')};
  font-size: ${(props: any) => themeUtilities.getFontSize(props.theme, 'default')}px;
  width: 100%;
  display: flex;
  align-items: center;
  background: ${(props: any) => themeUtilities.getRgbaColor(props.theme, 'input.backgroundColor')};
`

interface StyledInputContainerProps {
  children?: React.ReactNode
  error?: string
  label?: string
  required?: boolean
}

const StyledInputContainer = ({ children, error, required, label }: StyledInputContainerProps) => {
  return (
    <StyledFlexRow>
      {/* @ts-expect-error */}
      <StyledRequiredIndicator isRequired={required} />
      <StyledFlexColumn>
        {label && <StyledLabel>{label}</StyledLabel>}
        {children}
        {error && <StyledErrorMessage>{error}</StyledErrorMessage>}
      </StyledFlexColumn>
    </StyledFlexRow>
  )
}

interface StyledInputProps {
  children?: React.ReactNode
  error?: string
  icon?: React.ReactNode
  label?: string
  required?: boolean
}

const StyledInput = React.forwardRef<HTMLInputElement, StyledInputProps>((props, ref) => {
  const { icon, children, error, required, label } = props
  if (icon) {
    return (
      <StyledInputContainer label={label} error={error} required={required}>
        <StyledInputWrapper data-id="wg-input-wrapper">
          <VanillaInput {...props} ref={ref}>
            {children}
          </VanillaInput>
          {icon}
        </StyledInputWrapper>
      </StyledInputContainer>
    )
  } else {
    return (
      <StyledInputContainer label={label} error={error} required={required}>
        <StyledInputComponent {...props} ref={ref}>
          {children}
        </StyledInputComponent>
      </StyledInputContainer>
    )
  }
})

StyledInput.displayName = 'StyledInput'

export default StyledInput
export { StyledInputContainer }
